import React, { useState, useEffect } from "react";
import {
  Grid,
  Typography,
  Paper,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField
} from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import axios from "axios";
import * as XLSX from "xlsx"; // NEW

function toISODate(dt) {
  return dt
    ? new Date(dt).toLocaleDateString("en-CA", { timeZone: "Asia/Jakarta" }) // YYYY-MM-DD
    : "";
}

// Pretty Jakarta local datetime for Excel (YYYY-MM-DD HH:mm:ss)
function toJakartaDateTime(dt) {
  if (!dt) return "";
  try {
    return new Date(dt).toLocaleString("sv-SE", {
      timeZone: "Asia/Jakarta",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false
    }).replace("T", " ");
  } catch {
    return String(dt);
  }
}

export default function MarketingReview() {
  const [platform, setPlatform] = useState(""); // kept for parity with your params
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [result, setResult] = useState([]);
  const [details, setDetails] = useState([]); // NEW: holds full rows for export
  const [skip, setSkip] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [costs, setCosts] = useState({});
  const [selectedAd, setSelectedAd] = useState(null);
  const [cleanupStatus, setCleanupStatus] = useState("");

  const limit = 40;

  const fetchData = async (reset = false) => {
    if (reset) {
      setResult([]);
      setSkip(0);
    }
    setLoading(true);
    setError("");
    try {
      const resp = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/SonarAdsReview`,
        {
          params: {
            platform,
            startDate: toISODate(startDate),
            endDate: toISODate(endDate),
            skip: reset ? 0 : skip,
            limit
          }
        }
      );
      // Summary (table)
      const data = resp.data.summary || [];
      setResult(prev => (reset ? data : [...prev, ...data]));
      setSkip(prev => (reset ? data.length : prev + data.length));
      setHasMore(data.length === limit);

      // Details (export) — NEW
      setDetails(resp.data.details || []);
    } catch (e) {
      setError("Failed to fetch data");
    }
    setLoading(false);
  };

  const handleCleanup = async () => {
    setCleanupStatus("");
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/cleanup-orphan-tickets`
      );
      setCleanupStatus(data.message || "Cleanup completed");
    } catch {
      setCleanupStatus("Cleanup failed");
    }
  };

  const handleSearch = () => fetchData(true);
  const handleLoadMore = () => {
    if (!loading && hasMore) fetchData(false);
  };
  const handleCostChange = (adCode, val) => {
    setCosts(prev => ({ ...prev, [adCode]: val.replace(/\D/g, "") }));
  };

  // CPL = cost / inquiryCount
  const calcCPL = row => {
    const cost = parseFloat(costs[row.adCode] || 0);
    const cnt = row.inquiryCount || 0;
    if (!cost || !cnt) return "-";
    return (cost / cnt).toLocaleString();
  };

  // NEW: Download Excel of details
  const handleDownloadExcel = () => {
    if (!details || details.length === 0) return;

    // Shape rows for Excel; keep each variable as a column
    const rows = details.map((d) => ({
      "Tanggal (Asia/Jakarta)": toJakartaDateTime(d.tanggal),
      AdCode: d.AdCode ?? "",
      Nama: d.Nama ?? "",
      Phone: d.Phone ?? "",
      Kebutuhan: d.Kebutuhan ?? ""
    }));

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Details");

    const s = toISODate(startDate) || "1970-01-01";
    const e = toISODate(endDate) || new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Jakarta" });
    const filename = `SonarDetails_${s}_to_${e}.xlsx`;

    XLSX.writeFile(wb, filename);
  };

  const handleRowClick = async row => {
    setSelectedAd({ loading: true });
    try {
      const codeParam = row.adCode.startsWith("Unknown") ? null : row.adCode;
      const { data } = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/adDetails`,
        { params: { adCode: codeParam } }
      );
      setSelectedAd(data);
    } catch {
      setSelectedAd({ error: "Failed to load details" });
    }
  };

  useEffect(() => {}, []);

  return (
    <Grid container>
      <Grid item xs={12} sm={9} md={12} sx={{ p: 2 }}>
        <Typography variant="h5" gutterBottom>
          Marketing Review
        </Typography>

        <Paper sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={6} sm={3}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Start Date"
                  value={startDate}
                  onChange={setStartDate}
                  renderInput={params => <TextField {...params} fullWidth />}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={6} sm={3}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="End Date"
                  value={endDate}
                  onChange={setEndDate}
                  renderInput={params => <TextField {...params} fullWidth />}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12} sm="auto">
              <Button
                variant="contained"
                onClick={handleSearch}
                disabled={loading}
              >
                Search
              </Button>
            </Grid>

            {/* NEW: Download Excel button */}
            <Grid item xs={12} sm="auto">
              <Button
                variant="outlined"
                onClick={handleDownloadExcel}
                disabled={loading || details.length === 0}
              >
                Download Excel (Details)
              </Button>
            </Grid>
          </Grid>

          {error && (
            <Typography color="error" sx={{ mt: 1 }}>
              {error}
            </Typography>
          )}
          {cleanupStatus && (
            <Typography sx={{ mt: 1 }}>
              {cleanupStatus}
            </Typography>
          )}
        </Paper>

        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>AdCode</TableCell>
                <TableCell>Cost (Rp)</TableCell>
                <TableCell align="right">Inquiry</TableCell>
                <TableCell align="right">CPL</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {result.map(row => (
                <TableRow
                  key={row.adCode}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() => handleRowClick(row)}
                >
                  <TableCell>{row.adCode}</TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      value={costs[row.adCode] || ""}
                      onChange={e => handleCostChange(row.adCode, e.target.value)}
                      inputProps={{ inputMode: "numeric" }}
                    />
                  </TableCell>
                  <TableCell align="right">{row.inquiryCount}</TableCell>
                  <TableCell align="right">{calcCPL(row)}</TableCell>
                </TableRow>
              ))}
              {!loading && result.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    No data
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {hasMore && (
          <Box textAlign="center" my={2}>
            <Button onClick={handleLoadMore} disabled={loading}>
              {loading ? "Loading..." : "Load More"}
            </Button>
          </Box>
        )}
      </Grid>
    </Grid>
  );
}
